import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { constant } from 'constant';

export const apiSlice = createApi({
	reducerPath: 'api',
	baseQuery: fetchBaseQuery({
		baseUrl: `${constant['baseUrl']}api`,
		prepareHeaders: (headers) => {
			const token =
				localStorage.getItem('accessToken') ||
				sessionStorage.getItem('accessToken');
			if (token) {
				headers.set('Authorization', `Bearer ${token}`);
			}
			const tenantId =
				localStorage.getItem('tenantId') || sessionStorage.getItem('tenantId');
			if (tenantId) {
				headers.set('x-tenant-id', tenantId);
			}

			return headers;
		},
	}),
	tagTypes: ['Items'],
	endpoints: (builder) => ({
		fetchItems: builder.query({
			query: ({ path, params }) => {
				const queryString = new URLSearchParams(params).toString();
				return `${path}?${queryString}`;
			},
			providesTags: (result, error, { path, params }) => {
				const queryString = new URLSearchParams(params).toString();
				const cacheKey = `${path}?${queryString}`;

				if (result && Array.isArray(result)) {
					return [
						...result.map(({ id }) => ({ type: 'Items', id })),
						{ type: 'Items', id: cacheKey },
					];
				}

				if (result && typeof result === 'object' && result.data) {
					return [
						...(Array.isArray(result.data)
							? result.data.map(({ id }) => ({ type: 'Items', id }))
							: []),
						{ type: 'Items', id: cacheKey },
					];
				}

				return [{ type: 'Items', id: cacheKey }];
			},
			keepUnusedDataFor: 10, // 10 seconds
		}),

		createItem: builder.mutation({
			// `idempotencyKey` (optional): sent as the Idempotency-Key header so the
			// finance backend can de-duplicate retries/double-clicks of the same
			// submission. IMPORTANT: the backend stores every final response (2xx
			// AND 4xx) under key + hash(method|path|body), so a single fixed key
			// must NOT be reused across an EDITED body — that returns 409 "already
			// used for a different request". Derive the key from the body via
			// `useIdempotentSubmit().keyFor(body)` (finance-core/_ui/hooks): equal
			// bodies share a key (safe de-dupe), a changed body mints a fresh one.
			query: ({ path, body, idempotencyKey }) => ({
				url: path,
				method: 'POST',
				body,
				...(idempotencyKey ? { headers: { 'Idempotency-Key': idempotencyKey } } : {}),
			}),
		}),

		// PUT request
		updateItem: builder.mutation({
			query: ({ path, body }) => ({
				url: path,
				method: 'PUT',
				body,
			}),
		}),

		// PATCH request
		patchItem: builder.mutation({
			query: ({ path, body }) => ({
				url: path,
				method: "PATCH",
				body,
			}),
		}),

		// DELETE request
		deleteItem: builder.mutation({
			query: ({ path, body }) => ({
				url: path,
				method: 'DELETE',
				body,
			}),
		}),

		deleteManyInvoices: builder.mutation({
			query: ({ path, body }) => ({
				url: path,
				method: 'DELETE',
				body,
			}),
			invalidatesTags: ['Invoices'],
		}),

		downloadInvoice: builder.mutation({
			query: ({ invoiceNo }) => ({
				url: `/invoices/download/${invoiceNo}`,
				method: 'POST',
				responseHandler: (response) => response.blob(),
			}),
		}),
		// createLeadQualification: builder.mutation({
		//   query: ({ leadId, createdBy, body }) => ({
		//     url: `/lead/qualifications`,
		//     method: "POST",
		//     params: {
		//       lead: leadId,
		//       createdBy,
		//     },
		//     body,
		//   }),
		// }),
		createLeadQualification: builder.mutation({
			query: ({ leadId, createdBy, body }) => ({
				url: `/lead/qualifications`,
				method: 'POST',
				params: { lead: leadId, createdBy },
				body,
			}),
			// Invalidate the tag for this lead so getLeadQualification refetches
			invalidatesTags: (result, error, { leadId }) => [
				{ type: 'Items', id: leadId },
			],
		}),

		// getLeadQualification: builder.query({
		//   query: ({ leadId, createdBy }) => ({
		//     url: `/lead/qualifications`,
		//     method: "GET",
		//     params: { lead: leadId, createdBy },
		//   }),
		//   transformResponse: (response) => response.doc, // returns array
		//   providesTags: (result, error, { leadId }) => [
		//     { type: "Items", id: leadId },
		//   ],
		// }),
		getLeadQualification: builder.query({
			query: ({ leadId, createdBy }) => ({
				url: `/lead/qualifications/lead/${leadId}`,
				method: "GET",
				// params: { lead: leadId },
			}),
			transformResponse: (response) => response.doc, // returns array
			providesTags: (result, error, { leadId }) => [
				{ type: "Items", id: leadId },
			],
		}),
		updateLeadQualification: builder.mutation({
			query: ({ qualificationId, leadId, createdBy, body }) => ({
				url: `/lead/qualifications/${qualificationId}`,
				method: "PUT",
				params: {
					lead: leadId,
					createdBy,
				},
				body,
			}),
			invalidatesTags: (result, error, { leadId }) => [
				{ type: "Items", id: leadId },
			],
		}),
		fetchItemsV2: builder.query({
			query: ({ path, params }) => {
				const queryString = new URLSearchParams(params).toString();
				return `${path}?${queryString}`;
			},
			keepUnusedDataFor: 0,
		}),
		downloadFile: builder.query({
			query: ({ path }) => ({
				url: path,
				method: "GET",
				responseHandler: (response) => response.blob(),
			}),
		}),
		// A POST that behaves like a QUERY: cached, deduped and refetchable by
		// its (path, body) args — not a mutation. Needed for read endpoints whose
		// request is a structured body rather than a querystring, e.g. the
		// Insights `/query` batch (a panel list + cohort list + filter object
		// would not survive as URL params).
		postQuery: builder.query({
			query: ({ path, body }) => ({ url: path, method: 'POST', body: body || {} }),
			keepUnusedDataFor: 30,
		}),
	}),
});

export const {
	useFetchItemsQuery,
	useCreateItemMutation,
	useUpdateItemMutation,
	usePatchItemMutation,
	useDeleteItemMutation,
	useDeleteManyInvoicesMutation,
	useDownloadInvoiceMutation,
	useCreateLeadQualificationMutation,
	useGetLeadQualificationQuery,
	useUpdateLeadQualificationMutation,
	useLazyFetchItemsV2Query,
	useDownloadFileQuery,
	usePostQueryQuery,
	useLazyPostQueryQuery,
} = apiSlice;